/**
 * ONE TRUTH for the numeric handling of extended-entity property edits, shared
 * by BOTH the SHELL validator (`extendedPropertyMutations.ts`, command-engine)
 * and the DOCUMENT translator (`propertyTranslator.ts`, apps/web).
 *
 * Codex P2 (#417) — before this module the two sides classified "which mapped
 * field is numeric" AND parsed "is this string a number" DIFFERENTLY:
 *
 *   • The shell numeric gate was keyed only off the RANGE-bounded labels
 *     (`numericLabelConstraints` = MText Height/Width, Text Height), so an
 *     UNBOUNDED numeric field (MText `Rotation`/`Line spacing`, Multileader
 *     `Leader type`) slipped through: `Rotation="abc"` was accepted by the shell
 *     (preview mutated) while the document OMITTED it (the schema-derived
 *     `numericFieldsByType` classifies it numeric) → palette showed a value the
 *     canvas / DXF never persisted.
 *   • The document treated only the NARROW decimal regex `/^-?\d+(?:\.\d+)?$/` as
 *     numeric and otherwise OMITTED a known-numeric field, while the shell
 *     accepted ANY finite `Number(value)` — so `Height=" 5 "` / `".5"` / `"1e3"`
 *     were accepted by the shell but dropped by the document → divergence.
 *
 * This module is the single source of truth for BOTH axes:
 *   (A) the label→field map (`labelToFieldByType`), the schema-derived
 *       numeric-field set (`numericFieldsByType`), and the per-field range bounds
 *       (`numericFieldConstraints`) — so the shell can ask, by LABEL, the SAME
 *       questions the document asks by FIELD; and
 *   (B) the numeric GRAMMAR (`parseFiniteNumeric`) used on BOTH sides.
 *
 * Placement: command-engine (which already depends on drawing-model for the zod
 * schemas) — `apps/web` depends on command-engine, so the document translator
 * imports it via the public barrel and the shell validator imports it relatively.
 * command-engine never imports apps/web, so no dep-graph cycle is introduced.
 */

import { z } from "zod";
import {
  dimensionEntitySchema,
  mtextEntitySchema,
  multileaderEntitySchema,
  revisionCloudEntitySchema,
  textEntitySchema,
} from "@dxf-ai-editor/drawing-model";

/**
 * ONE numeric GRAMMAR for BOTH the shell and the document. Accepts any value
 * that JavaScript parses to a FINITE number after trimming surrounding
 * whitespace — `" 5 "` → 5, `".5"` → 0.5, `"1e3"` → 1000, `"-2.5"` → -2.5 — and
 * rejects `""` / `"abc"` / `"  "` (→ null). This replaces the divergent pair of
 * graders the two sides used before:
 *   • shell: `Number(value)` + `Number.isFinite` (accepts `" 5 "`/`.5`/`1e3`)
 *   • document: the narrow `/^-?\d+(?:\.\d+)?$/` regex (rejected the same three)
 * so a value accepted by the shell is now ALWAYS persisted by the document and
 * vice-versa.
 *
 * NOTE: `Number("")` is 0 (finite), so the explicit empty-after-trim guard is
 * load-bearing — a cleared cell must REJECT, not coerce to a spurious 0.
 */
export function parseFiniteNumeric(value: string): number | null {
  const trimmed = value.trim();
  if (trimmed === "") return null;
  const n = Number(trimmed);
  return Number.isFinite(n) ? n : null;
}

/**
 * Per-field numeric schema range bounds, FIELD-keyed (one truth for both sides).
 *
 * Audit (drawing-model model.ts) of EVERY numeric field any `labelToFieldByType`
 * entry maps to. Only fields with a `.positive()` / `.nonnegative()` / `.min()` /
 * `.max()` bound get an entry; a plain `z.number()` (any finite value valid) is
 * intentionally absent so the path is not over-constrained.
 *   • MText  nominalHeight   z.number().positive()    → { exclusiveMin: 0 }
 *   • MText  referenceWidth  z.number().nonnegative() → { min: 0 }
 *   • Text   height          z.number().nonnegative() → { min: 0 }
 * Unconstrained (plain z.number(), no entry): Dimension textHeight /
 * textRotation / obliqueAngle / leaderLength; Text rotation; MText rotation /
 * lineSpacingFactor; Multileader leaderLineType / textHeight / textRotation /
 * textWidth / arrowheadSize / landingGap / doglegLength / blockScale /
 * blockRotation / textLineSpacingFactor / textLineSpacingDistance.
 */
export const numericFieldConstraints: Record<
  string,
  { exclusiveMin?: number; min?: number; max?: number }
> = {
  nominalHeight: { exclusiveMin: 0 },
  referenceWidth: { min: 0 },
  height: { min: 0 },
  // REVCLOUD-PROPS-EDITOR (§5.1, @codex #487 P2) — the revcloud arc-length fields
  // now ride the generic label→field map, so a Properties-grid / CHPROP / direct
  // `properties/apply` edit reaches the document translator. Without a bound here
  // the shared `satisfiesNumericConstraint` would accept 0 / -5, persisting a
  // degenerate arc length OUTSIDE the special editor (the form + the descriptor
  // both require `exclusiveMin: 0`). Mirror that bound so every write path — form,
  // grid, CLI, AI — rejects a non-positive arc length identically. The schema
  // (`z.number().default(15)`) is intentionally left unbounded (source round-trip
  // must not reject imported data); this is the EDIT-path bound, like nominalHeight.
  approximateArcLength: { exclusiveMin: 0 },
  arcChordLength: { exclusiveMin: 0 },
};

/** True when `value` satisfies the field's numeric range bound (if any). */
export function satisfiesNumericConstraint(field: string, value: number): boolean {
  const c = numericFieldConstraints[field];
  if (c === undefined) return true;
  if (c.exclusiveMin !== undefined && !(value > c.exclusiveMin)) return false;
  if (c.min !== undefined && value < c.min) return false;
  if (c.max !== undefined && value > c.max) return false;
  return true;
}

/**
 * Map UI property labels → typed entity field paths, per entity type. Only
 * TOP-LEVEL scalar/string fields live here (nested point coordinates and other
 * complex fields require dedicated parsing in the document translator). EVERY
 * mapped field name MUST be a real key of that entity's typed zod schema
 * (`drawing-model` model.ts) — a name that does NOT exist on the schema becomes a
 * STRAY key on the patch that `patchEntityById`'s shallow merge silently drops,
 * so the real field never updates and the edit is a silent no-op (the MText
 * Height/Width key bug, same class as the #416 Contents bug). The drift-guard
 * test `propertyTranslator.driftGuard.test.ts` asserts every mapped field is a
 * key of the corresponding schema `.shape`.
 */
export const labelToFieldByType: Record<
  string,
  Record<string, string>
> = {
  Dimension: {
    "Dimension style": "styleName",
    "Text override": "dimensionText",
    "Text height": "textHeight",
    "Text rotation": "textRotation",
    "Oblique angle": "obliqueAngle",
    "Leader length": "leaderLength",
  },
  Text: {
    "Text style": "styleName",
    Contents: "contents",
    // Text's `Height: "height"` IS correct — the TEXT schema HAS a `height`
    // field (group 40, `z.number().nonnegative()`).
    Height: "height",
    Rotation: "rotation",
  },
  MText: {
    "Text style": "styleName",
    Contents: "contents",
    // The MText schema (`mtextEntitySchema`, drawing-model model.ts) has NO
    // `height` / `width` field — the Properties-grid "Height" / "Width" rows
    // DISPLAY `nominalHeight` (group 40, `z.number().positive()`) and
    // `referenceWidth` (group 41, `z.number().nonnegative()`). Both are NUMERIC
    // z.number fields, so they ride the unified finite-number coercion (the
    // grid's formatted-number string → number) and MUST NOT be added to
    // `stringOnlyFields`.
    Height: "nominalHeight",
    Width: "referenceWidth",
    Rotation: "rotation",
    "Line spacing": "lineSpacingFactor",
  },
  // REVCLOUD-PROPS-EDITOR (§5.1) — the revision-cloud special editor persists its
  // fields through `properties/apply` → the legacy document translator
  // (`translateExtendedEntityPropertyChanges`, propertyTranslator.ts), which reads
  // THIS map. Revision Cloud is NOT in the DEFUSE-S15 typed cutover
  // (`TYPED_EXTENDED_GEOMETRY_KINDS`) — it carries an `enum` creationMode leaf the
  // typed writer defers (raw-int enum guard) and a `closed` boolean the shell never
  // patches — so it rides the legacy translator like every other non-cutover kind.
  // `revisionCloudStyle` / `creationMode` are string/enum fields (kept verbatim by
  // the translator's non-numeric fallback); `approximateArcLength` / `arcChordLength`
  // are z.number → numericFieldsByType classifies them numeric → the translator
  // coerces via `parseFiniteNumeric`. The render adapter re-tessellates the cloud
  // from the persisted `approximateArcLength` each frame, so the arc edit is
  // visibly the "regen arcs" AutoCAD performs on Apply.
  "Revision Cloud": {
    "Cloud style": "revisionCloudStyle",
    "Approximate arc length": "approximateArcLength",
    "Minimum arc length": "arcChordLength",
    "Creation mode": "creationMode",
  },
  Multileader: {
    // ── Style & content ──
    "Multileader style": "styleName",
    Contents: "textContents",
    "Text height": "textHeight",
    "Text rotation": "textRotation",
    "Text color": "textColor",
    "Text width": "textWidth",
    // ── Leader visuals ──
    "Arrowhead size": "arrowheadSize",
    "Arrowhead block": "arrowheadBlock",
    "Leader color": "leaderLineColor",
    "Leader weight": "leaderLineWeight",
    "Leader type": "leaderLineType",
    "Landing gap": "landingGap",
    "Dogleg length": "doglegLength",
    // ── Block content ──
    "Block name": "blockName",
    "Block scale": "blockScale",
    "Block rotation": "blockRotation",
    "Block color": "blockColor",
    // ── Text attachment and frame ──
    "Text attachment": "textAttachmentType",
    "Text direction": "textAttachmentDirection",
    "Text frame": "textFrameDisplay",
    "Line spacing style": "textLineSpacingStyle",
    "Line spacing factor": "textLineSpacingFactor",
    "Line spacing distance": "textLineSpacingDistance",
    // ── Override flags (numeric bitfield) ──
    "Override flags": "propertyOverrideFlags",
    // NOTE: Coordinate fields (Arrow X/Y, Landing X/Y) are nested-point entries
    // that require dedicated parsing — intentionally NOT mapped here.
  },
};

/**
 * Schema-derived numeric classification: a mapped field is numeric iff its zod
 * type unwraps to `z.ZodNumber` (through any Default/Optional/Nullable/Catch
 * wrapper). This auto-covers EVERY mapped numeric — constrained
 * (`nominalHeight`/`referenceWidth`/Text `height`) AND unconstrained
 * (`rotation`/`lineSpacingFactor`/`textHeight`/`leaderLineType`/Multileader
 * sizes) — and stays correct if a schema field's type changes. `contents` /
 * `textContents` (ZodString) and `textFrameDisplay` (ZodBoolean) are classified
 * non-numeric, so the dedicated string-only / boolean handling keeps owning them.
 */
function unwrapZodNumeric(schema: z.ZodTypeAny): boolean {
  let cur: unknown = schema;
  for (let i = 0; i < 12; i++) {
    if (cur instanceof z.ZodNumber) return true;
    const def = (cur as { _def?: { type?: string; innerType?: unknown } })._def;
    if (!def) return false;
    if (
      def.type === "default" ||
      def.type === "optional" ||
      def.type === "nullable" ||
      def.type === "catch"
    ) {
      cur = def.innerType;
      continue;
    }
    return false;
  }
  return false;
}

/**
 * @codex #487 P2 — schema-derived ENUM classification (mirrors `unwrapZodNumeric`):
 * unwrap the Default/Optional/Nullable/Catch wrappers and, when the leaf is a
 * `z.ZodEnum`, return its allowed value list — else null. This lets the shared
 * classifier reject a value outside a mapped enum field's domain BY CONSTRUCTION
 * from the schema (Revision Cloud `creationMode` = `z.enum(["Rectangular",
 * "Polygonal", "Object"])`), so the shell and the document agree without a
 * hand-copied option list that could drift from the schema.
 */
function unwrapZodEnumValues(schema: z.ZodTypeAny): readonly string[] | null {
  let cur: unknown = schema;
  for (let i = 0; i < 12; i++) {
    if (cur instanceof z.ZodEnum) return cur.options as readonly string[];
    const def = (cur as { _def?: { type?: string; innerType?: unknown } })._def;
    if (!def) return null;
    if (
      def.type === "default" ||
      def.type === "optional" ||
      def.type === "nullable" ||
      def.type === "catch"
    ) {
      cur = def.innerType;
      continue;
    }
    return null;
  }
  return null;
}

const schemaByMappedType: Record<string, z.ZodObject<z.ZodRawShape>> = {
  Dimension: dimensionEntitySchema,
  Text: textEntitySchema,
  MText: mtextEntitySchema,
  Multileader: multileaderEntitySchema,
  "Revision Cloud": revisionCloudEntitySchema,
};

/**
 * Per-type set of mapped field names whose typed schema field is NUMERIC. Built
 * once from `labelToFieldByType` × the entity zod schema `.shape`. The document
 * coercion path consults this to OMIT (never string-fallback) a non-finite value
 * for a known-numeric field; the shell consults it (by LABEL, via
 * `classifyNumericLabel`) to reject the SAME values so neither side mutates while
 * the other drops. Exported so a drift-guard test can assert the classification
 * matches the schema.
 */
export const numericFieldsByType: Record<string, ReadonlySet<string>> = (() => {
  const out: Record<string, Set<string>> = {};
  for (const [type, labelMap] of Object.entries(labelToFieldByType)) {
    const schema = schemaByMappedType[type];
    if (!schema) continue;
    const shape = schema.shape;
    const numeric = new Set<string>();
    for (const field of Object.values(labelMap)) {
      const fieldSchema = shape[field] as z.ZodTypeAny | undefined;
      if (fieldSchema && unwrapZodNumeric(fieldSchema)) numeric.add(field);
    }
    out[type] = numeric;
  }
  return out;
})();

/**
 * @codex #487 P2 — per-type ENUM classification with each enum field's allowed
 * value domain. Built once from `labelToFieldByType` × the entity zod schema
 * `.shape` (schema-derived — no hand-copied option list). Both sides consult it to
 * REJECT a value outside the enum domain identically (shell↔document parity): the
 * DOCUMENT translator OMITS an out-of-domain enum write (never persists an invalid
 * `creationMode`), and the SHELL rejects the same value into the preview, so
 * neither side keeps a value the other drops. Currently the only mapped enum leaf
 * is Revision Cloud `creationMode`; a future `z.enum` mapped field inherits this
 * gate by construction. Exported so a drift-guard test can assert the derivation
 * matches the schema.
 */
export const enumFieldsByType: Record<
  string,
  Record<string, ReadonlySet<string>>
> = (() => {
  const out: Record<string, Record<string, ReadonlySet<string>>> = {};
  for (const [type, labelMap] of Object.entries(labelToFieldByType)) {
    const schema = schemaByMappedType[type];
    if (!schema) continue;
    const shape = schema.shape;
    const fields: Record<string, ReadonlySet<string>> = {};
    for (const field of Object.values(labelMap)) {
      const fieldSchema = shape[field] as z.ZodTypeAny | undefined;
      if (!fieldSchema) continue;
      const values = unwrapZodEnumValues(fieldSchema);
      if (values) fields[field] = new Set(values);
    }
    if (Object.keys(fields).length > 0) out[type] = fields;
  }
  return out;
})();

/**
 * @codex #487 P2 — string-DOMAIN classification for mapped fields whose typed
 * schema stores a FIXED value set as a PLAIN `z.string()` (not a `z.enum`), so
 * `unwrapZodEnumValues` cannot derive the domain from the schema. The only such
 * field today is Revision Cloud `revisionCloudStyle`
 * (`z.string().default("Normal")`): the DXF REVCLOUD encoder only has codes for
 * `Normal` (0) / `Calligraphy` (1) and maps ANY other value to `Normal`, so a
 * free-text style like `"Foo"` is stored during the session but silently
 * saves/reloads as `Normal`, and a numeric-looking `"1"` from the Properties
 * grid would be coerced to the NUMBER `1` by the generic translator tail
 * (violating the `z.string()` schema). This map is the ONE TRUTH for that domain,
 * consulted by BOTH the shell validator (via `classifyStringDomainLabel`) and the
 * document translator (via `stringDomainFieldsByType` directly), so both KEEP the
 * value string-typed AND reject an out-of-domain value in lockstep — exactly like
 * the enum gate, but for a `z.string()` field.
 *
 * Domain source of truth: the dxf-io REVCLOUD style encoder
 * (`REVCLOUD_STYLE_TO_CODE` keys = Normal / Calligraphy). That encoder lives in
 * the leaf `dxf-io` package (command-engine does not depend on it), so the two
 * AutoCAD-fixed style names are mirrored here with a drift-guard unit test
 * (`numericFieldClassification.revcloudStyleDomain.test.ts`) — the same
 * mirror-with-guard pattern `numericFieldConstraints` uses for the schema bounds.
 */
export const stringDomainFieldsByType: Record<
  string,
  Record<string, ReadonlySet<string>>
> = {
  "Revision Cloud": {
    revisionCloudStyle: new Set(["Normal", "Calligraphy"]),
  },
};

/**
 * @codex #487 P2 — the shell-side counterpart of `classifyEnumLabel` for a
 * string-DOMAIN field (Revision Cloud `Cloud style` → `revisionCloudStyle`). For
 * a (type, LABEL) it returns the mapped field + its allowed value domain when the
 * label is a KNOWN string-domain mapped field for that type, else `null`. The
 * shell rejects a value outside the domain, the SAME reject the document makes by
 * consulting `stringDomainFieldsByType[type][field]` directly — so a
 * numeric-looking (`"1"`) or out-of-domain (`"Foo"`) `Cloud style` from the
 * Properties grid / CHPROP / direct `properties/apply` is dropped on BOTH sides
 * in lockstep instead of being coerced to a number or silently saved as `Normal`.
 */
export function classifyStringDomainLabel(
  entityType: string,
  label: string,
): { field: string; allowed: ReadonlySet<string> } | null {
  const field = labelToFieldByType[entityType]?.[label];
  if (field === undefined) return null;
  const allowed = stringDomainFieldsByType[entityType]?.[field];
  if (allowed === undefined) return null;
  return { field, allowed };
}

/**
 * The shell validator works with property LABELS ("Height", "Rotation", "Leader
 * type"), the document translator with schema FIELDS ("nominalHeight",
 * "rotation", "leaderLineType"). This bridges them: for a (type, LABEL) it
 * returns the mapped numeric field + its range constraint when the label is a
 * KNOWN-NUMERIC mapped field for that type, else `null`. Both sides derive their
 * numeric verdict from this ONE function so they classify identically:
 *   • document: keys off `numericFieldsByType[type].has(field)` directly (it
 *     already has the field), then `numericFieldConstraints[field]`.
 *   • shell: only has the label → calls this to learn (a) is it numeric, and
 *     (b) which range bound applies — the SAME bound the document uses.
 */
export function classifyNumericLabel(
  entityType: string,
  label: string,
): { field: string; constraint?: { exclusiveMin?: number; min?: number; max?: number } } | null {
  const field = labelToFieldByType[entityType]?.[label];
  if (field === undefined) return null;
  if (!(numericFieldsByType[entityType]?.has(field) ?? false)) return null;
  return { field, constraint: numericFieldConstraints[field] };
}

/**
 * @codex #487 P2 — the shell-side counterpart of `classifyNumericLabel` for ENUM
 * fields. The shell validator works with property LABELS ("Creation mode"), the
 * document translator with schema FIELDS ("creationMode") — this bridges them: for
 * a (type, LABEL) it returns the mapped enum field + its allowed value domain when
 * the label is a KNOWN-ENUM mapped field for that type, else `null`. The shell
 * asks this to reject a value outside the enum domain, the SAME reject the document
 * makes by consulting `enumFieldsByType[type][field]` directly — so a direct
 * `properties/apply` (agent/CLI/scripted) with an out-of-domain enum value
 * (`Creation mode: "Freehand"`) is dropped on BOTH sides in lockstep instead of
 * the shell keeping a value the document omits (and instead of the document
 * persisting an invalid `RevisionCloudEntity` in PROD, where re-validation is
 * skipped).
 */
export function classifyEnumLabel(
  entityType: string,
  label: string,
): { field: string; allowed: ReadonlySet<string> } | null {
  const field = labelToFieldByType[entityType]?.[label];
  if (field === undefined) return null;
  const allowed = enumFieldsByType[entityType]?.[field];
  if (allowed === undefined) return null;
  return { field, allowed };
}
